<template>
  <div v-if="paymentSchedule">
    <div class="schedule" role="table" aria-label="График платежей">
      <div class="schedule__header" role="row">
        <div class="schedule__cell schedule__cell--head" role="columnheader">
          Дата платежа
        </div>
        <div class="schedule__cell schedule__cell--head" role="columnheader">
          Выплата процентов
        </div>
        <div class="schedule__cell schedule__cell--head" role="columnheader">
          Выплата основного долга
        </div>
        <div class="schedule__cell schedule__cell--head" role="columnheader">
          Дата оплаты
        </div>
        <div class="schedule__cell schedule__cell--head" role="columnheader">
          Статус
        </div>
      </div>

      <div
        v-for="(item, index) in paymentSchedule.payments"
        :key="index"
        class="schedule__row"
        :class="{ 'schedule__row--buyback': index === buybackRowIndex }"
        role="row"
      >
        <div class="schedule__cell" role="cell">
          <div class="schedule__cell-stack">
            <date-format :date="item.paymentDate" />
            <span
              v-if="index === buybackRowIndex"
              class="schedule__buyback-badge"
            >
              Откуп: <date-format :date="buybackDate" />
            </span>
          </div>
        </div>
        <div class="schedule__cell" role="cell">
          <money :money="item.interestPayment" />
        </div>
        <div class="schedule__cell" role="cell">
          <money :money="item.principalRepayment" />
        </div>
        <div class="schedule__cell" role="cell">
          <span v-if="item.paidAt"><date-format :date="item.paidAt" /></span>
          <span v-else class="schedule__muted">—</span>
        </div>
        <div class="schedule__cell" role="cell">
          <span :class="statusClass(getPaymentStatus(item).code)">
            {{ getPaymentStatus(item).label }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import moment from "moment";
import DateFormat from "@/components/common/components/DateFormat.vue";
import Money from "@/components/common/components/Money.vue";
import { ProjectType } from "@/utils/api-client/enums/ProjectType";

export default {
  name: "PaymentSchedule",
  components: { Money, DateFormat },
  props: {
    paymentSchedule: {
      type: Object,
      required: true,
      validator: (value) => Array.isArray(value.payments),
    },
    projectType: {
      type: String,
      default: ProjectType.LOAN,
    },
    lastPaymentUuid: {
      type: String,
      required: true,
    },
    graceDays: {
      type: Number,
      default: 3,
    },
    buybackDate: {
      type: String,
      default: null,
    },
  },
  methods: {
    isPaymentOverdue(payment) {
      const paymentDate = moment(payment.paymentDate).startOf("day");
      const refDate = payment.paidAt
        ? moment(payment.paidAt).startOf("day")
        : moment().startOf("day");

      const refMinusGrace = moment(refDate).subtract(this.graceDays, "days");
      return refMinusGrace.isAfter(paymentDate);
    },
    getPaymentStatus(item) {
      const overdue = this.isPaymentOverdue(item);

      if (this.projectType === ProjectType.PROJECT && item.paid) {
        if (this.lastPaymentUuid && !(this.lastPaymentUuid === item.uuid)) {
          return { code: "on-time", label: "Оплачен досрочно" };
        }
      }

      if (item.paid) {
        if (overdue) {
          return { code: "upcoming", label: "Оплачен с просрочкой" };
        }
        return { code: "on-time", label: "Оплачен вовремя" };
      }

      if (item.overduePayment) {
        return { code: "overdue", label: "Просрочка" };
      }
      return { code: "upcoming", label: "" };
    },

    statusClass(code) {
      return {
        "status--overdue": code === "overdue",
        "status--on-time": code === "on-time",
        "status--upcoming": code === "upcoming",
      };
    },
  },
  computed: {
    buybackRowIndex() {
      if (!this.buybackDate || !this.paymentSchedule?.payments?.length) {
        return -1;
      }
      const payments = this.paymentSchedule.payments;
      const buyback = moment(this.buybackDate);

      const sameMonthIndex = payments.findIndex((payment) =>
        moment(payment.paymentDate).isSame(buyback, "month")
      );
      if (sameMonthIndex !== -1) {
        return sameMonthIndex;
      }

      const nextPaymentIndex = payments.findIndex((payment) =>
        moment(payment.paymentDate).isSameOrAfter(buyback, "day")
      );
      if (nextPaymentIndex !== -1) {
        return nextPaymentIndex;
      }

      return payments.length - 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule {
  background: var(--white);
  border: 1px solid var(--stroke-on-white-color);
  border-radius: 0.8rem;
  overflow: hidden;

  &__header,
  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr 1.2fr 1fr 1fr;
    gap: 1rem;
    align-items: center;
  }

  &__header {
    position: sticky;
    text-transform: uppercase;
  }

  &__row {
    border-top: 1px solid var(--stroke-on-white-color);
    transition: background-color 120ms ease;
  }

  &__cell {
    padding: 0.8rem;
    font-size: 1rem;
    color: var(--dark);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    &--head {
      font-weight: 500;
      color: var(--secondary-gray);
      font-size: 0.8rem;
    }
  }
}
.status--on-time {
  color: var(--primary-green-color-hover);
}

.status--upcoming {
  color: var(--color-warning-border);
}

.status--overdue {
  color: var(--color-error-border);
}

.schedule__cell-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}

.schedule__row--buyback {
  background: rgba(5, 183, 104, 0.05);
}

.schedule__buyback-badge {
  margin-left: 0.8rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary-green-color);
  white-space: nowrap;
}
</style>
